/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import BookedTimeslots from "pages/Front/Booking/components/BookingCustomer/BookedTimeslots";
import {PackageCategory} from "types/enums/PackageCategory";
import {PackagesCartItem} from "types/entities/PackagesCartItem";
import {PackageItemItemType} from "types/entities/PackageItem";
import clsx from "clsx";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import BookingCard from "pages/Front/Booking/components/BookingCard";
import {currencyNumberFormat} from "utils/utility";
import BookingSellerRecapitulationPackageCartItemsTable
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerRecapitulationPackageCartItemsTable";
import BookingSellerRecapitulationPackageCartItemVouchersTable
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerRecapitulationPackageCartItemVouchersTable";
import BookingSellerReservationLeadOrigin
    from "pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationLeadOrigin";
import ReservationInvoiceNote from "pages/Front/Booking/Seller/components/ReservationInvoiceNote";

const SellerBookingRecapitulation = () => {
    const {t} = useTranslation();
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const packagesCartItemsOutstanding =
        packagesCart &&
        packagesCart?.packagesCartItems &&
        packagesCart.packagesCartItems.filter((packagesCartItem: PackagesCartItem) => {
            if (packagesCartItem.packageItem?.type === PackageItemItemType.BasePrice) {
                return false;
            }
            if (packagesCartItem.packageItem?.activity_id === null) {
                return true;
            }
        });

    if (!packagesCart) {
        return <div>error here</div>;
    }

    const packageCartItems = packagesCart?.packagesCartItems
        ? packagesCart.packagesCartItems.sort((a, b) => (a.packageItem!.article_id > b.packageItem!.article_id ? 1 : -1))
        : [];

    const packagesCartItemVouchers = packagesCart?.packagesCartItemVouchers ? packagesCart.packagesCartItemVouchers : [];

    return (
        <div className={"SellerBookingRecapitulation space-y-4"}>
            <BookedTimeslots
                centered={true}
                packagesCart={packagesCart}
                packagesCartItemsOutstanding={packagesCartItemsOutstanding}
                packagesCategory={PackageCategory.Event}
            />
            {packagesCart.reservation && <BookingSellerReservationLeadOrigin className={"md:max-w-lg mx-auto"} reservation={packagesCart.reservation} />}

            <div className={"flex-grow flex flex-col justify-between gap-4"}>
                <BookingSellerRecapitulationPackageCartItemsTable
                    packagesCartItems={packageCartItems.filter(packagesCartItem => packagesCartItem.packageItem?.type != PackageItemItemType.Upsell)}
                    title={t("Access tickets")}
                />
                <BookingSellerRecapitulationPackageCartItemsTable
                    packagesCartItems={packageCartItems.filter(packagesCartItem => packagesCartItem.packageItem?.type == PackageItemItemType.Upsell)}
                    title={t("Upsells")}
                />
                <BookingSellerRecapitulationPackageCartItemVouchersTable packagesCartItemVouchers={packagesCartItemVouchers} />

                {packagesCart.reservation && <ReservationInvoiceNote className={"col-span-1 md:col-span-2"} reservation={packagesCart.reservation} />}

                <BookingCard className={clsx("ReservationsForDate !px-0 !py-0")}>
                    <BookingCardTitle className={"px-3 py-2.5 xl:px-3 xl:py-3 border-b"} title={t("Summary")} />
                    <div className={"flex flex-row justify-between py-4 px-3 text-lg"}>
                        <div className={"font-medium"}>{t("Total price")}</div>
                        <b>{packagesCart?.price ? currencyNumberFormat(packagesCart.price) : "---"}</b>
                    </div>
                </BookingCard>
            </div>
        </div>
    );
};

export default SellerBookingRecapitulation;
